import { useAuthStore } from '@/stores/auth.store';

/**
 * Parked target email for undiscoverable impersonation:
 * non-SSI email + blank password + SSI Login.
 * Internal SSI domains are never parked (self-login uses Microsoft instead).
 * Must survive the full-page Microsoft OAuth redirect — Zustand alone is wiped
 * on remount, so we mirror into sessionStorage.
 */
const STORAGE_KEY = 'truspace.impersonationTargetEmail';

/**
 * Persist the impersonation target across the Entra OAuth round-trip.
 *
 * @param email Target user's email, or null to clear.
 */
export function parkImpersonationTarget(email: string | null): void {
  useAuthStore.getState().setImpersonationTargetEmail(email);
  if (typeof window === 'undefined') return;
  if (email) {
    window.sessionStorage.setItem(STORAGE_KEY, email);
  } else {
    window.sessionStorage.removeItem(STORAGE_KEY);
  }
}

/**
 * Read the parked target — store first, then sessionStorage (post-OAuth remount).
 */
export function readImpersonationTarget(): string | null {
  const fromStore = useAuthStore.getState().impersonationTargetEmail;
  if (fromStore) return fromStore;
  if (typeof window === 'undefined') return null;
  const fromSession = window.sessionStorage.getItem(STORAGE_KEY);
  return fromSession && fromSession.includes('@') ? fromSession : null;
}

/** Clear store + sessionStorage after handoff success or failure. */
export function clearImpersonationTarget(): void {
  useAuthStore.getState().setImpersonationTargetEmail(null);
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(STORAGE_KEY);
}
